import React, { useMemo, useState } from "react";
import { toast } from "sonner";
import { FileDown, X, Sparkles } from "lucide-react";
import { useFinance } from "@/context/FinanceContext";
import { monthlySummary } from "@/lib/insights";
import { exportInsightPdf } from "@/lib/exportInsightPdf";
import { cn } from "@/lib/utils";

const ACK_KEY = "fcs_monthly_recap_ack";

function monthKey(d) {
  return `${d.getFullYear()}-${d.getMonth()}`;
}

// Start-of-month nudge: offers a one-tap PDF of LAST month's summary, generated
// entirely on-device. Appears once per month until downloaded or dismissed.
export function MonthlySummaryPrompt({ className }) {
  const { finance } = useFinance();
  const currentKey = monthKey(new Date());
  const [acked, setAcked] = useState(() => {
    try {
      return localStorage.getItem(ACK_KEY) === currentKey;
    } catch {
      return false;
    }
  });

  const last = useMemo(() => monthlySummary(finance, -1), [finance]);

  if (acked || !last || !last.hasData) return null;

  const ack = () => {
    try {
      localStorage.setItem(ACK_KEY, currentKey);
    } catch {
      /* ignore */
    }
    setAcked(true);
  };

  const download = () => {
    try {
      exportInsightPdf(finance, { offset: -1 });
      toast.success(`Ringkasan ${last.monthLabel} diunduh (PDF).`);
    } catch {
      toast.error("Gagal membuat PDF. Coba lagi.");
    }
    ack();
  };

  return (
    <div
      className={cn(
        "rounded-2xl border border-emerald-200 bg-emerald-50 p-4",
        className
      )}
      data-testid="monthly-recap-prompt"
    >
      <div className="flex items-start gap-3">
        <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white shadow-sm">
          <Sparkles className="h-4 w-4 text-emerald-600" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-heading text-sm font-bold text-emerald-900" data-testid="monthly-recap-title">
            Ringkasan {last.monthLabel} siap
          </p>
          <p className="mt-1 text-xs leading-relaxed text-emerald-700">
            Bulan baru sudah dimulai. Simpan rekap keuangan bulan lalu sebagai PDF —
            dibuat langsung di perangkat Anda.
          </p>
          <button
            data-testid="monthly-recap-download"
            onClick={download}
            className="mt-3 flex h-10 w-full items-center justify-center gap-2 rounded-full bg-emerald-600 text-sm font-semibold text-white transition-colors hover:bg-emerald-700"
          >
            <FileDown className="h-4 w-4" /> Unduh Ringkasan Bulan Lalu (PDF)
          </button>
        </div>
        <button
          data-testid="monthly-recap-dismiss"
          onClick={ack}
          className="shrink-0 text-emerald-400 transition-colors hover:text-emerald-600"
          aria-label="Tutup"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
